import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Drop, Leaf, Wrench, Warning, Megaphone, Plus, CaretLeft, CaretRight, ArrowsLeftRight, X, CheckCircle, Cloud, Sun } from "@phosphor-icons/react";
import { toast } from "sonner";

// Olive + buff + terracotta — editorial allotment
const MOSS = "#5A6B3E";
const MOSS_DEEP = "#3E4A2B";
const BUFF = "#EFEAD8";
const BUFF_LIGHT = "#F7F3E4";
const TERRA = "#B36A3E";
const INK = "#242016";

const MEMBERS = [
  { id: "m1", name: "Priya", color: MOSS_DEEP },
  { id: "m2", name: "Jim", color: TERRA },
  { id: "m3", name: "Hannah", color: "#7C8B5C" },
  { id: "m4", name: "Miles", color: "#4E6169" },
  { id: "m5", name: "Anita", color: "#8A4E3E" },
];

// Rota across 3 weeks — shapes subtly different so navigating feels real
const WEEKS = [
  {
    num: 31,
    label: "Week 31 · mid-July",
    weather: { icon: Sun, note: "Very dry · water twice daily" },
    beds: [
      { id: 1, label: "Bed 1 — Tomatoes", schedule: "Mon · Thu", owner: "m1" },
      { id: 3, label: "Bed 3 — Salad", schedule: "Tue · Fri", owner: "m2" },
      { id: 5, label: "Bed 5 — Herbs", schedule: "Wed · Sat", owner: "m5" },
      { id: 7, label: "Bed 7 — Beans", schedule: "Mon · Thu", owner: "m3" },
      { id: 9, label: "Bed 9 — Brassicas", schedule: "Sun · Wed", owner: "m4" },
    ],
  },
  {
    num: 32,
    label: "Week 32 · July heatwave",
    weather: { icon: Sun, note: "Heatwave · extra evening water on Tue & Fri" },
    beds: [
      { id: 1, label: "Bed 1 — Tomatoes", schedule: "Mon · Thu", owner: "m1" },
      { id: 3, label: "Bed 3 — Salad", schedule: "Tue · Fri", owner: "m2" },
      { id: 5, label: "Bed 5 — Herbs", schedule: "Wed · Sat", owner: "m3" },
      { id: 7, label: "Bed 7 — Beans", schedule: "Mon · Thu", owner: null, cover: "Away — please cover" },
      { id: 9, label: "Bed 9 — Brassicas", schedule: "Sun · Wed", owner: "m4" },
    ],
  },
  {
    num: 33,
    label: "Week 33 · rain forecast",
    weather: { icon: Cloud, note: "Rain most days — skip watering unless dry spell" },
    beds: [
      { id: 1, label: "Bed 1 — Tomatoes", schedule: "Mon · Thu", owner: "m3" },
      { id: 3, label: "Bed 3 — Salad", schedule: "Tue · Fri", owner: "m2" },
      { id: 5, label: "Bed 5 — Herbs", schedule: "Wed · Sat", owner: "m5" },
      { id: 7, label: "Bed 7 — Beans", schedule: "Mon · Thu", owner: "m1" },
      { id: 9, label: "Bed 9 — Brassicas", schedule: "Sun · Wed", owner: "m4" },
    ],
  },
];

const KIT_SEED = [
  { id: "k1", name: "Strimmer", heldBy: "m2", since: 72, overdueAfter: 48 },
  { id: "k2", name: "Long hose", heldBy: null, since: 0, overdueAfter: 24 },
  { id: "k3", name: "Wheelbarrow", heldBy: "m1", since: 6, overdueAfter: 48 },
  { id: "k4", name: "Hedge shears", heldBy: null, since: 0, overdueAfter: 24 },
  { id: "k5", name: "Seed trays (stack)", heldBy: "m3", since: 28, overdueAfter: 96 },
];

const NOTICES_SEED = [
  { id: "n1", who: "m3", text: "Courgette glut — help yourselves from the shed shelf. Third one this week!", when: "2h ago" },
  { id: "n2", who: "m4", text: "I'll be away Fri–Sun — please water Bed 9 twice. Thanks.", when: "yesterday" },
  { id: "n3", who: "m1", text: "Committee work day Saturday 10am — weeding the communal strip. Cake provided.", when: "2d ago" },
];

const TABS = [
  { key: "rota", label: "Watering rota", icon: Drop },
  { key: "kit", label: "Shared kit", icon: Wrench },
  { key: "board", label: "Noticeboard", icon: Megaphone },
];

export default function AllotmentRota() {
  const [tab, setTab] = useState("rota");
  const [weekIdx, setWeekIdx] = useState(1); // start on week 32
  const [weeks, setWeeks] = useState(WEEKS);
  const [kit, setKit] = useState(KIT_SEED);
  const [notices, setNotices] = useState(NOTICES_SEED);
  const navigate = useNavigate();

  const member = (id) => MEMBERS.find((m) => m.id === id);

  const coverBed = (bedId) => {
    setWeeks((ws) => ws.map((w, i) => i !== weekIdx ? w : { ...w, beds: w.beds.map((b) => b.id === bedId ? { ...b, owner: "me", cover: null } : b) }));
    toast.success(`Covering Bed ${bedId} this week — thanks!`);
  };

  const swapBeds = (aId, bId) => {
    setWeeks((ws) => ws.map((w, i) => {
      if (i !== weekIdx) return w;
      const a = w.beds.find((b) => b.id === aId);
      const b = w.beds.find((x) => x.id === bId);
      if (!a || !b) return w;
      return {
        ...w,
        beds: w.beds.map((x) => {
          if (x.id === aId) return { ...x, owner: b.owner };
          if (x.id === bId) return { ...x, owner: a.owner };
          return x;
        }),
      };
    }));
    toast.success(`Swapped Bed ${aId} ↔ Bed ${bId}`);
  };

  const returnKit = (kitId) => {
    setKit((ks) => ks.map((k) => (k.id === kitId ? { ...k, heldBy: null, since: 0 } : k)));
    toast.success("Logged as returned to shed");
  };
  const borrowKit = (kitId) => {
    setKit((ks) => ks.map((k) => (k.id === kitId ? { ...k, heldBy: "me", since: 0 } : k)));
    toast.success("Enjoy — 48h loan");
  };
  const addKit = (name) => {
    if (!name.trim()) return;
    setKit((ks) => [{ id: "k" + Date.now(), name, heldBy: null, since: 0, overdueAfter: 48 }, ...ks]);
    toast.success(`${name} added to the shed inventory`);
  };

  const addNotice = (text) => {
    if (!text.trim()) return;
    setNotices((ns) => [{ id: "n" + Date.now(), who: "me", text, when: "just now" }, ...ns]);
    toast.success("Pinned to the board");
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: BUFF, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="allotment-rota-interactive">
      <header className="border-b" style={{ backgroundColor: BUFF_LIGHT, borderColor: "#D6CFB4" }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/allotment-rota" className="opacity-50" style={{ color: INK }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: MOSS }}>
              <Leaf size={16} weight="fill" color={BUFF_LIGHT} />
            </div>
            <div>
              <div className="text-base font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>Willow Fields Allotments <span className="font-mono text-[10px] opacity-60 font-normal">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">12 plots · 5 members active</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=allotment-rota")} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: MOSS_DEEP, color: MOSS_DEEP }} data-testid="allotment-book-cta">Talk to us about this</button>
        </div>
        <nav className="max-w-5xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: "#D6CFB4" }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-medium flex items-center gap-1.5" style={{ color: tab === t.key ? INK : "#7C7562" }} data-testid={`allotment-tab-${t.key}`}>
                <Icon size={14} weight="fill" /> {t.label}
                {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: MOSS }} />}
              </button>
            );
          })}
        </nav>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-8">
        {tab === "rota" && <RotaTab weeks={weeks} weekIdx={weekIdx} setWeekIdx={setWeekIdx} member={member} coverBed={coverBed} swapBeds={swapBeds} />}
        {tab === "kit" && <KitTab kit={kit} member={member} returnKit={returnKit} borrowKit={borrowKit} addKit={addKit} />}
        {tab === "board" && <BoardTab notices={notices} member={member} addNotice={addNotice} />}
      </section>

      <footer className="border-t mt-8 py-10" style={{ backgroundColor: BUFF_LIGHT, borderColor: "#D6CFB4" }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · designed to be calm, not another app that buzzes</p>
          <p className="max-w-md mx-auto text-sm opacity-80 mb-5 italic" style={{ fontFamily: "'Outfit', serif" }}>"Weekly digest on Sunday night. Nudges only when something's genuinely overdue."</p>
          <Link to="/consultation?demo=allotment-rota" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: MOSS_DEEP }}>Book a 30-min consultation <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>
    </div>
  );
}

function RotaTab({ weeks, weekIdx, setWeekIdx, member, coverBed, swapBeds }) {
  const week = weeks[weekIdx];
  const WIcon = week.weather.icon;
  const [swapFrom, setSwapFrom] = useState(null); // bed id the user tapped first

  const startSwap = (bedId) => {
    setSwapFrom(bedId);
  };
  const completeSwap = (bedId) => {
    if (!swapFrom || swapFrom === bedId) { setSwapFrom(null); return; }
    swapBeds(swapFrom, bedId);
    setSwapFrom(null);
  };
  const cancelSwap = () => setSwapFrom(null);

  return (
    <div>
      {/* Week navigator */}
      <div className="flex items-center justify-between mb-2" data-testid="allotment-week-nav">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">{week.label}</p>
          <h1 className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Watering rota</h1>
        </div>
        <div className="flex items-center gap-1 bg-white border-2 p-1" style={{ borderColor: "#D6CFB4" }}>
          <button onClick={() => setWeekIdx(Math.max(0, weekIdx - 1))} disabled={weekIdx === 0} className="w-8 h-8 flex items-center justify-center disabled:opacity-30" style={{ color: MOSS_DEEP }} data-testid="allotment-week-prev">
            <CaretLeft size={16} weight="bold" />
          </button>
          <span className="text-sm font-mono font-bold px-3" style={{ color: MOSS_DEEP }}>Wk {week.num}</span>
          <button onClick={() => setWeekIdx(Math.min(weeks.length - 1, weekIdx + 1))} disabled={weekIdx === weeks.length - 1} className="w-8 h-8 flex items-center justify-center disabled:opacity-30" style={{ color: MOSS_DEEP }} data-testid="allotment-week-next">
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Weather banner */}
      <div className="mb-5 p-3 flex items-center gap-2 text-sm" style={{ backgroundColor: BUFF_LIGHT, border: `1px dashed ${MOSS}` }}>
        <WIcon size={16} weight="fill" style={{ color: week.weather.icon === Sun ? TERRA : MOSS_DEEP }} />
        <span><b>{week.num === 33 ? "Forecast" : "This week"}:</b> {week.weather.note}</span>
      </div>

      {/* Swap banner */}
      {swapFrom && (
        <div className="mb-4 p-3 flex items-center gap-3 text-sm" style={{ background: MOSS_DEEP, color: BUFF_LIGHT }} data-testid="allotment-swap-banner">
          <ArrowsLeftRight size={16} weight="bold" />
          <span className="flex-1">Swapping <b>Bed {swapFrom}</b> — now tap another bed to swap with.</span>
          <button onClick={cancelSwap} className="opacity-70 hover:opacity-100" data-testid="allotment-swap-cancel"><X size={16} /></button>
        </div>
      )}

      <div className="space-y-3" data-testid="allotment-rota-list">
        {week.beds.map((b) => {
          const owner = b.owner === "me" ? { name: "You", color: MOSS_DEEP } : member(b.owner);
          const needsCover = !owner;
          const isSwapTarget = swapFrom && swapFrom !== b.id;
          const isSwapFrom = swapFrom === b.id;
          return (
            <div key={b.id} className="bg-white border-2 p-4 flex items-center gap-4 transition-all" style={{ borderColor: isSwapFrom ? MOSS_DEEP : isSwapTarget ? MOSS : needsCover ? TERRA : "#D6CFB4", boxShadow: isSwapFrom ? `0 0 0 3px ${MOSS_DEEP}22` : "none" }} data-testid={`allotment-bed-${b.id}`}>
              <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center font-black text-lg" style={{ backgroundColor: needsCover ? TERRA + "22" : MOSS + "22", color: needsCover ? TERRA : MOSS_DEEP, fontFamily: "'Outfit', sans-serif" }}>
                {b.id}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{b.label}</h3>
                <p className="text-xs opacity-70 font-mono">{b.schedule}</p>
              </div>
              {owner ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: owner.color }}>{owner.name[0]}</div>
                    <span className="text-sm font-semibold">{owner.name}</span>
                  </div>
                  {isSwapTarget ? (
                    <button onClick={() => completeSwap(b.id)} className="text-xs font-semibold px-3 py-2 text-white" style={{ backgroundColor: MOSS_DEEP }} data-testid={`allotment-swap-target-${b.id}`}>
                      Swap here
                    </button>
                  ) : isSwapFrom ? (
                    <button onClick={cancelSwap} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: MOSS_DEEP, color: MOSS_DEEP }}>
                      Cancel
                    </button>
                  ) : (
                    <button onClick={() => startSwap(b.id)} className="text-xs font-semibold px-3 py-2 border opacity-70 hover:opacity-100 flex items-center gap-1" style={{ borderColor: MOSS_DEEP, color: MOSS_DEEP }} data-testid={`allotment-swap-start-${b.id}`}>
                      <ArrowsLeftRight size={12} weight="bold" /> Swap
                    </button>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xs font-bold" style={{ color: TERRA }}>{b.cover}</div>
                  </div>
                  <button onClick={() => coverBed(b.id)} className="text-xs font-semibold px-3 py-2 text-white" style={{ backgroundColor: MOSS_DEEP }} data-testid={`allotment-cover-${b.id}`}>
                    I'll cover
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs opacity-60 mt-6 italic">Tap <b>Swap</b> on any bed, then tap the bed you want to swap with. Sunday night digest summarises the week.</p>
    </div>
  );
}

function KitTab({ kit, member, returnKit, borrowKit, addKit }) {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">What's where</p>
          <h1 className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Shared kit</h1>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="text-xs font-semibold text-white px-3 py-2 flex items-center gap-1.5" style={{ backgroundColor: MOSS_DEEP }} data-testid="allotment-add-kit-open">
            <Plus size={12} weight="bold" /> Add kit
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-white border-2 p-4 mb-5 flex items-center gap-2" style={{ borderColor: MOSS_DEEP }} data-testid="allotment-add-kit-form">
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. Electric mower" className="flex-1 p-2 border text-sm" style={{ borderColor: "#D6CFB4", backgroundColor: BUFF_LIGHT }} data-testid="allotment-add-kit-name" />
          <button onClick={() => { addKit(draft); setDraft(""); setAdding(false); }} className="text-xs font-semibold text-white px-4 py-2" style={{ backgroundColor: MOSS_DEEP }} data-testid="allotment-add-kit-save">Save</button>
          <button onClick={() => { setDraft(""); setAdding(false); }} className="text-xs opacity-60 hover:opacity-100 px-2 py-2">Cancel</button>
        </div>
      )}

      <div className="space-y-3" data-testid="allotment-kit-list">
        {kit.map((k) => {
          const holder = k.heldBy === "me" ? { name: "You", color: MOSS_DEEP } : member(k.heldBy);
          const overdue = k.heldBy && k.since > k.overdueAfter;
          return (
            <div key={k.id} className="bg-white border-2 p-4 flex items-center gap-4" style={{ borderColor: overdue ? TERRA : "#D6CFB4" }} data-testid={`allotment-kit-${k.id}`}>
              <Wrench size={22} weight="duotone" style={{ color: overdue ? TERRA : MOSS_DEEP }} className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{k.name}</h3>
                {k.heldBy ? (
                  <p className="text-xs font-mono opacity-70">With {holder.name} · {formatHours(k.since)}{overdue ? <span className="font-bold" style={{ color: TERRA }}> · overdue</span> : ""}</p>
                ) : (
                  <p className="text-xs font-mono opacity-70">In the shed</p>
                )}
              </div>
              {overdue && <Warning size={20} weight="fill" style={{ color: TERRA }} />}
              <div className="flex gap-2">
                {k.heldBy ? (
                  <button onClick={() => returnKit(k.id)} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: MOSS_DEEP, color: MOSS_DEEP }} data-testid={`allotment-kit-return-${k.id}`}>Return</button>
                ) : (
                  <button onClick={() => borrowKit(k.id)} className="text-xs font-semibold px-3 py-2 text-white" style={{ backgroundColor: MOSS_DEEP }} data-testid={`allotment-kit-borrow-${k.id}`}>Borrow · 48h</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatHours(h) {
  if (h < 1) return "just now";
  if (h < 24) return `${Math.round(h)}h`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"}`;
}

function BoardTab({ notices, member, addNotice }) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Communal</p>
      <h1 className="text-4xl font-black tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Noticeboard</h1>

      <div className="bg-white border-2 p-4 mb-6" style={{ borderColor: "#D6CFB4" }}>
        <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Harvest to share? Going away? Leave a note for the site…" className="w-full p-3 border text-sm" style={{ borderColor: "#D6CFB4", backgroundColor: BUFF_LIGHT }} data-testid="allotment-notice-input" />
        <div className="flex justify-end mt-2">
          <button onClick={() => { addNotice(draft); setDraft(""); }} className="text-xs font-semibold text-white px-4 py-2 flex items-center gap-1.5" style={{ backgroundColor: MOSS_DEEP }} data-testid="allotment-notice-pin"><Plus size={12} weight="bold" /> Pin to board</button>
        </div>
      </div>

      <div className="space-y-3" data-testid="allotment-notice-list">
        {notices.map((n) => {
          const who = n.who === "me" ? { name: "You", color: MOSS_DEEP } : member(n.who);
          return (
            <article key={n.id} className="bg-white border-2 p-4 flex gap-4" style={{ borderColor: "#D6CFB4" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ backgroundColor: who.color }}>{who.name[0]}</div>
              <div className="flex-1">
                <div className="flex items-baseline gap-2">
                  <b style={{ fontFamily: "'Outfit', sans-serif" }}>{who.name}</b>
                  <span className="text-[10px] font-mono opacity-60 uppercase tracking-widest">{n.when}</span>
                </div>
                <p className="text-sm mt-1 leading-relaxed">{n.text}</p>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
